import { useState, useEffect } from 'react'
import { Navigate, Link } from 'react-router-dom'
import { Plane, Monitor, Plus, Pencil, Trash2, ExternalLink, Copy, X, LogOut, ShieldCheck, MapPin, Cpu } from 'lucide-react'
import { useAuth } from '../hooks/useAuth'
import { logout } from '../lib/auth'
import { getMyDisplays, createDisplay, updateDisplay, deleteDisplay, claimDevice } from '../lib/api'
import LocationPicker from '../components/LocationPicker'

const MODES = [
  { value: 'dashboard', label: 'Dashboard' },
  { value: 'matrix', label: 'Matrix' },
  { value: 'fids', label: 'Airport FIDS' },
  { value: 'map', label: 'Radar Map' },
  { value: 'minimal', label: 'Minimal' },
]

const DEFAULT_LOCATION = { lat: 51.5074, lon: -0.1278 } // London

function displayUrl(display) {
  const params = new URLSearchParams()
  params.set('mode', display.mode)
  if (display.mode === 'fids') {
    params.set('airport', display.airport || 'LHR')
  } else {
    params.set('radius', display.radius || 20)
    if (display.lat != null && display.lon != null) {
      params.set('lat', display.lat)
      params.set('lon', display.lon)
    }
  }
  return `${window.location.origin}/display/${display.id}?${params}`
}

export default function Dashboard() {
  const { user, loading, isAdmin } = useAuth()
  const [displays, setDisplays] = useState([])
  const [displaysLoading, setDisplaysLoading] = useState(true)
  const [showCreate, setShowCreate] = useState(false)
  const [showPair, setShowPair] = useState(false)
  const [editing, setEditing] = useState(null)
  const [error, setError] = useState('')

  const load = () => getMyDisplays().then(data => { setDisplays(data || []); setDisplaysLoading(false) }).catch(() => setDisplaysLoading(false))

  useEffect(() => { if (user) load() }, [user])

  if (loading) return <div className="min-h-screen bg-slate-950 flex items-center justify-center"><div className="w-8 h-8 border-2 border-flight-cyan border-t-transparent rounded-full animate-spin" /></div>
  if (!user) return <Navigate to="/login" replace />

  const handleCreate = async (display) => {
    setError('')
    try {
      await createDisplay(display)
      setShowCreate(false)
      load()
    } catch (err) {
      setError(err.message)
    }
  }

  const handleUpdate = async (display) => {
    setError('')
    try {
      await updateDisplay(editing.id, { ...display, active: editing.active ?? true })
      setEditing(null)
      load()
    } catch (err) {
      setError(err.message)
    }
  }

  const handleDelete = async (id) => {
    if (!confirm('Delete this display?')) return
    await deleteDisplay(id)
    load()
  }

  const handlePair = async (payload) => {
    setError('')
    try {
      await claimDevice(payload)
      setShowPair(false)
      load()
    } catch (err) {
      setError(err.message)
    }
  }

  return (
    <div className="min-h-screen bg-slate-950">
      <header className="border-b border-slate-800">
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <Plane className="w-5 h-5 text-flight-cyan" />
            <span className="text-lg font-bold text-white">FlightWall</span>
          </Link>
          <div className="flex items-center gap-4">
            {isAdmin && <Link to="/admin" className="flex items-center gap-1.5 text-sm text-flight-violet hover:text-flight-violet/80"><ShieldCheck className="w-4 h-4" /> Admin panel</Link>}
            <span className="text-slate-400 text-sm">{user.email}</span>
            <button onClick={logout} className="p-2 rounded-lg text-slate-400 hover:text-white hover:bg-slate-800"><LogOut className="w-4 h-4" /></button>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 py-8">
        <div className="mb-8 flex items-center justify-between">
          <div><h1 className="text-2xl font-bold text-white">My Displays</h1><p className="text-slate-400 text-sm">Create a display, then open its link on any TV, monitor, or tablet</p></div>
          <div className="flex items-center gap-3">
            <button onClick={() => setShowPair(true)} className="px-4 py-2 rounded-lg border border-slate-700 text-white font-medium text-sm flex items-center gap-2 hover:bg-slate-800">
              <Cpu className="w-4 h-4" /> Pair a Device
            </button>
            <button onClick={() => setShowCreate(true)} className="px-4 py-2 rounded-lg bg-flight-cyan text-slate-950 font-bold text-sm flex items-center gap-2 hover:bg-flight-cyan/90">
              <Plus className="w-4 h-4" /> New Display
            </button>
          </div>
        </div>

        {displaysLoading ? (
          <div className="flex items-center justify-center py-16"><div className="w-8 h-8 border-2 border-flight-cyan border-t-transparent rounded-full animate-spin" /></div>
        ) : displays.length === 0 ? (
          <div className="glass-panel p-12 text-center">
            <Monitor className="w-8 h-8 mx-auto mb-3 text-slate-600" />
            <p className="text-slate-400 mb-4">No displays yet</p>
            <button onClick={() => setShowCreate(true)} className="px-6 py-3 rounded-lg bg-flight-cyan text-slate-950 font-bold text-sm hover:bg-flight-cyan/90">Create your first display</button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {displays.map(d => (
              <DisplayCard key={d.id} display={d} onDelete={() => handleDelete(d.id)} onEdit={() => setEditing(d)} />
            ))}
          </div>
        )}
      </main>

      {showCreate && <DisplayModal onSave={handleCreate} onClose={() => { setShowCreate(false); setError('') }} error={error} />}
      {editing && <DisplayModal display={editing} onSave={handleUpdate} onClose={() => { setEditing(null); setError('') }} error={error} />}
      {showPair && <PairDeviceModal onPair={handlePair} onClose={() => { setShowPair(false); setError('') }} error={error} />}
    </div>
  )
}

function DisplayCard({ display, onDelete, onEdit }) {
  const url = displayUrl(display)
  const [copied, setCopied] = useState(false)

  const copy = () => {
    navigator.clipboard.writeText(url)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  return (
    <div className="glass-panel p-5">
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg bg-slate-800 flex items-center justify-center"><Monitor className="w-4 h-4 text-flight-cyan" /></div>
          <div>
            <div className="text-white font-medium">{display.name}</div>
            <div className="text-slate-500 text-xs capitalize flex items-center gap-1">
              {display.mode} mode
              {display.mode === 'fids' ? (
                <span>• {display.airport}</span>
              ) : display.lat != null ? (
                <span className="flex items-center gap-0.5"><MapPin className="w-3 h-3" /> {display.lat.toFixed(2)}°, {display.lon.toFixed(2)}°</span>
              ) : (
                <span className="text-amber-500">• no location set</span>
              )}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-1">
          <button onClick={onEdit} className="p-1.5 rounded-lg text-slate-500 hover:text-white hover:bg-slate-800"><Pencil className="w-4 h-4" /></button>
          <button onClick={onDelete} className="p-1.5 rounded-lg text-slate-500 hover:text-red-400 hover:bg-slate-800"><Trash2 className="w-4 h-4" /></button>
        </div>
      </div>
      <div className="flex items-center gap-2">
        <a href={url} target="_blank" rel="noreferrer" className="flex-1 px-3 py-2 rounded-lg bg-slate-800 border border-slate-700 text-white text-sm text-center hover:bg-slate-700 flex items-center justify-center gap-2"><ExternalLink className="w-3.5 h-3.5" /> Open</a>
        <button onClick={copy} className="px-3 py-2 rounded-lg bg-slate-800 border border-slate-700 text-slate-300 hover:text-white text-sm flex items-center gap-2"><Copy className="w-3.5 h-3.5" /> {copied ? 'Copied' : 'Copy link'}</button>
      </div>
    </div>
  )
}

function DisplayModal({ display, onSave, onClose, error }) {
  const isEdit = !!display
  const [name, setName] = useState(display?.name || '')
  const [mode, setMode] = useState(display?.mode || 'dashboard')
  const [radius, setRadius] = useState(display?.radius || 20)
  const [airport, setAirport] = useState(display?.airport || 'LHR')
  const [location, setLocation] = useState(
    display?.lat != null ? { lat: display.lat, lon: display.lon } : DEFAULT_LOCATION
  )

  useEffect(() => {
    if (isEdit || !navigator.geolocation) return
    navigator.geolocation.getCurrentPosition(
      (pos) => setLocation({ lat: pos.coords.latitude, lon: pos.coords.longitude }),
      () => {},
      { timeout: 5000 }
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const submit = (e) => {
    e.preventDefault()
    onSave({ name, mode, radius: Number(radius), airport, lat: location.lat, lon: location.lon })
  }

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center p-4 z-50">
      <div className="glass-panel w-full max-w-lg max-h-[90vh] overflow-y-auto p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-lg font-bold text-white">{isEdit ? 'Edit Display' : 'New Display'}</h2>
          <button onClick={onClose} className="p-1 text-slate-400 hover:text-white"><X className="w-5 h-5" /></button>
        </div>
        <form onSubmit={submit} className="space-y-4">
          <div><label className="text-slate-400 text-sm mb-1 block">Name</label><input required type="text" value={name} onChange={e => setName(e.target.value)} placeholder="Living Room TV" className="w-full px-4 py-2 rounded-lg bg-slate-800 border border-slate-700 text-white focus:border-flight-cyan focus:outline-none" /></div>
          <div>
            <label className="text-slate-400 text-sm mb-1 block">Mode</label>
            <select value={mode} onChange={e => setMode(e.target.value)} className="w-full px-4 py-2 rounded-lg bg-slate-800 border border-slate-700 text-white focus:border-flight-cyan focus:outline-none">
              {MODES.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
            </select>
          </div>
          {mode === 'fids' ? (
            <div><label className="text-slate-400 text-sm mb-1 block">Airport (IATA code)</label><input type="text" value={airport} onChange={e => setAirport(e.target.value.toUpperCase())} maxLength={3} placeholder="LHR" className="w-full px-4 py-2 rounded-lg bg-slate-800 border border-slate-700 text-white uppercase focus:border-flight-cyan focus:outline-none" /></div>
          ) : (
            <>
              <div><label className="text-slate-400 text-sm mb-1 block">Radius (nautical miles)</label><input type="number" min="5" max="100" value={radius} onChange={e => setRadius(e.target.value)} className="w-full px-4 py-2 rounded-lg bg-slate-800 border border-slate-700 text-white focus:border-flight-cyan focus:outline-none" /></div>
              <div>
                <label className="text-slate-400 text-sm mb-1 block">Location</label>
                <LocationPicker lat={location.lat} lon={location.lon} onChange={(lat, lon) => setLocation({ lat, lon })} />
              </div>
            </>
          )}
          {error && <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/20 text-red-400 text-sm">{error}</div>}
          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={onClose} className="px-4 py-2 rounded-lg text-slate-400 hover:text-white text-sm">Cancel</button>
            <button type="submit" className="px-6 py-2 rounded-lg bg-flight-cyan text-slate-950 font-bold text-sm hover:bg-flight-cyan/90">{isEdit ? 'Save' : 'Create'}</button>
          </div>
        </form>
      </div>
    </div>
  )
}

function PairDeviceModal({ onPair, onClose, error }) {
  const [code, setCode] = useState('')
  const [name, setName] = useState('')
  const [radius, setRadius] = useState(20)
  const [location, setLocation] = useState(DEFAULT_LOCATION)

  const submit = (e) => {
    e.preventDefault()
    onPair({ pairing_code: code.toUpperCase(), name, radius: Number(radius), lat: location.lat, lon: location.lon })
  }

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center p-4 z-50">
      <div className="glass-panel w-full max-w-lg max-h-[90vh] overflow-y-auto p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-lg font-bold text-white">Pair a Device</h2>
          <button onClick={onClose} className="p-1 text-slate-400 hover:text-white"><X className="w-5 h-5" /></button>
        </div>
        <p className="text-slate-400 text-sm mb-4">Power on your FlightWall hardware. Once it connects to WiFi it will show a 6-character pairing code on its display. Enter it below.</p>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <label className="text-slate-400 text-sm mb-1 block">Pairing Code</label>
            <input required type="text" value={code} onChange={e => setCode(e.target.value.toUpperCase())} maxLength={6} placeholder="ABCDEF" className="w-full px-4 py-2 rounded-lg bg-slate-800 border border-slate-700 text-white uppercase tracking-widest font-mono text-center text-lg focus:border-flight-cyan focus:outline-none" />
          </div>
          <div><label className="text-slate-400 text-sm mb-1 block">Name</label><input required type="text" value={name} onChange={e => setName(e.target.value)} placeholder="Living Room Panel" className="w-full px-4 py-2 rounded-lg bg-slate-800 border border-slate-700 text-white focus:border-flight-cyan focus:outline-none" /></div>
          <div><label className="text-slate-400 text-sm mb-1 block">Radius (nautical miles)</label><input type="number" min="5" max="100" value={radius} onChange={e => setRadius(e.target.value)} className="w-full px-4 py-2 rounded-lg bg-slate-800 border border-slate-700 text-white focus:border-flight-cyan focus:outline-none" /></div>
          <div>
            <label className="text-slate-400 text-sm mb-1 block">Location</label>
            <LocationPicker lat={location.lat} lon={location.lon} onChange={(lat, lon) => setLocation({ lat, lon })} />
          </div>
          {error && <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/20 text-red-400 text-sm">{error}</div>}
          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={onClose} className="px-4 py-2 rounded-lg text-slate-400 hover:text-white text-sm">Cancel</button>
            <button type="submit" className="px-6 py-2 rounded-lg bg-flight-cyan text-slate-950 font-bold text-sm hover:bg-flight-cyan/90">Pair Device</button>
          </div>
        </form>
      </div>
    </div>
  )
}
